import { useEffect, useRef, type Dispatch } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { genUuid } from '@/lib/uuid';
import { SectionCard } from '../../anketa/components/SectionCard';
import { SectionHeader } from '../../anketa/components/SectionHeader';
import { isCompositionRowEmpty } from '../compositionRowUtils';
import type { CompositionField } from '../compositionRows';
import type { QuoteAction } from '../quoteReducer';
import type { CompositionRow, QuoteState } from '../types';

/**
 * Block 12 PR-D-A Phase D3 Step 4 — раздел «Позиции» (PRD §10.12.10 «Блок Состав»).
 *
 * 17 default-строк + custom additions. Прототип
 * `docs/prototypes/screen-2.12.10-cp.html` lines 147-187. Header «Позиции»
 * (не «Состав» — название в PRD §10.12.10:362 относится к разделу,
 * а на экране отображается «Позиции» per прототип:149).
 *
 * Layout матчит прототип:
 *  - heading row: «Позиции» (h2) + counter rows.length (text-sm muted)
 *  - column headers: Название (flex-2) / Тип (flex-1) / Бренд (flex-1) + spacer
 *  - editable inputs c dashed-underline (focus → solid + bg highlight)
 *  - Trash2 button per row (≥44px tap target, aria-label включает row name)
 *  - «+ Добавить строку самому» dashed-border button (min-h-14)
 *
 * Init pattern: useEffect dispatch COMPOSITION_INIT_DEFAULTS на mount.
 * Reducer idempotent — повторный init на existing state — no-op.
 * Phase D5 hydration (Step 9) сделает HYDRATE до того, как mount этой
 * секции; useEffect увидит non-empty compositionRows и не задиспатчит.
 */
export interface SectionCompositionProps {
  state: QuoteState;
  dispatch: Dispatch<QuoteAction>;
}

export function SectionComposition({ state, dispatch }: SectionCompositionProps) {
  const allRows = state.compositionRows;
  // PR-H1 Round 2 fix #1 — filter empty default rows для from-Anketa mode
  // (state.projectId !== null) чтобы скрыть placeholder rows из 17 defaults,
  // которые seedFromAnketa не заполнил. В standalone mode (projectId === null)
  // показываем все 17 placeholders — пользователь использует их как hint,
  // что можно заполнить. PDF render (renderQuoteToPdf.ts) всегда filter'ит
  // — output для клиента не должен содержать placeholder rows.
  // Custom rows (UUID IDs) никогда не filter'ятся — `isCompositionRowEmpty`
  // exempts non-default IDs (см. compositionRowUtils.ts).
  const visibleRows =
    state.projectId !== null ? allRows.filter((r) => !isCompositionRowEmpty(r)) : allRows;

  // One-time mount init — delete-to-zero не re-init'ит defaults.
  // Phase D5 hydration (Step 9) сделает HYDRATE до mount; useEffect увидит
  // non-empty compositionRows и skip dispatch. Если HYDRATE дойдёт после
  // первого render — initRef уже true, но HYDRATE сам наполнит state.
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    if (allRows.length === 0) dispatch({ type: 'COMPOSITION_INIT_DEFAULTS' });
  }, [allRows.length, dispatch]);

  const handleUpdate = (id: string, field: CompositionField, value: string) => {
    dispatch({ type: 'COMPOSITION_UPDATE', id, field, value });
  };
  const handleAdd = () => {
    dispatch({ type: 'COMPOSITION_ADD_CUSTOM', id: genUuid() });
  };
  const handleDelete = (id: string) => {
    dispatch({ type: 'COMPOSITION_DELETE', id });
  };

  return (
    <SectionCard testId="section-composition">
      <div className="flex items-center justify-between mb-3.5">
        <SectionHeader className="mb-0">Позиции</SectionHeader>
        <span
          className="text-sm text-muted"
          aria-label="Количество позиций"
          data-testid="composition-row-count"
        >
          {visibleRows.length}
        </span>
      </div>

      <div
        className="flex items-center gap-1.5 pb-2 mb-1 border-b-[1.5px] border-accent text-xs uppercase tracking-wider font-bold text-muted"
        role="row"
        aria-hidden="true"
      >
        <div className="flex-[2_1_0] min-w-0">Название</div>
        <div className="flex-[1_1_0] min-w-0">Тип</div>
        <div className="flex-[1_1_0] min-w-0">Бренд</div>
        <div className="w-11 flex-shrink-0" aria-hidden="true" />
      </div>

      <ul role="list" data-testid="composition-rows-list">
        {visibleRows.map((row) => (
          <CompositionRowItem
            key={row.id}
            row={row}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        ))}
      </ul>

      <button
        type="button"
        onClick={handleAdd}
        aria-label="Добавить строку самому"
        data-testid="composition-add-row"
        className="w-full min-h-14 mt-3.5 rounded-xl border-2 border-dashed border-accent bg-card text-ink text-base font-semibold flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
      >
        <Plus size={20} aria-hidden="true" />
        Добавить строку самому
      </button>
    </SectionCard>
  );
}

interface CompositionRowItemProps {
  row: CompositionRow;
  onUpdate: (id: string, field: CompositionField, value: string) => void;
  onDelete: (id: string) => void;
}

const CELL_INPUT_CLASS =
  'w-full min-h-11 px-1 py-1 bg-transparent border-0 border-b border-dashed border-accent/70 text-base text-ink placeholder:text-muted/60 placeholder:italic focus:border-solid focus:border-cta focus:bg-cta/10 focus:outline-none';

function CompositionRowItem({ row, onUpdate, onDelete }: CompositionRowItemProps) {
  const rowName = row.name.trim() || 'без названия';

  return (
    <li className="flex items-center gap-1.5 py-1 border-b border-canvas">
      <input
        type="text"
        value={row.name}
        onChange={(e) => onUpdate(row.id, 'name', e.target.value)}
        placeholder="—"
        aria-label={`Название позиции ${rowName}`}
        className={`${CELL_INPUT_CLASS} flex-[2_1_0] min-w-0 font-semibold`}
      />
      <input
        type="text"
        value={row.itemType}
        onChange={(e) => onUpdate(row.id, 'itemType', e.target.value)}
        placeholder="—"
        aria-label={`Тип для позиции ${rowName}`}
        className={`${CELL_INPUT_CLASS} flex-[1_1_0] min-w-0`}
      />
      <input
        type="text"
        value={row.brand}
        onChange={(e) => onUpdate(row.id, 'brand', e.target.value)}
        placeholder="—"
        aria-label={`Бренд для позиции ${rowName}`}
        className={`${CELL_INPUT_CLASS} flex-[1_1_0] min-w-0`}
      />
      <button
        type="button"
        onClick={() => onDelete(row.id)}
        aria-label={`Удалить позицию ${rowName}`}
        className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md text-destructive focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
      >
        <Trash2 size={18} aria-hidden="true" />
      </button>
    </li>
  );
}
